import { CommonModule } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import { ActivatedRoute, Router, RouterOutlet } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { NgxSpinnerService } from 'ngx-spinner';
import { take } from 'rxjs';
import { AutoRegistrationConfig } from './models/auto-registration-config';
import { CONFIG, registrationRoutesConfig } from './registration-config';
import { ToastrService } from 'ngx-toastr';
import { BusinessDetailsService, SharedService } from '../core';

@Component({
  selector: 'app-auto-registration',
  standalone: true,
  imports: [CommonModule, RouterOutlet],
  templateUrl: './auto-registration.component.html',
  styleUrls: ['./auto-registration.component.scss'],
})
export class AutoRegistrationComponent implements OnInit {
  dir: string = localStorage.getItem('lang') === 'en' ? 'ltr' : 'rtl';

  /** false until getRegisteredPhases completes; then show router-outlet */
  phasesCheckDone = false;

  /** True once the window 'message' handler has run (e.g. after receiving data from Glamera SAAS). */
  isAddEventListenerExecuted: boolean = false;

  constructor(
    public readonly translate: TranslateService,
    private readonly spinner: NgxSpinnerService,
    private readonly businessDetailsService: BusinessDetailsService,
    private readonly sharedService: SharedService,
    private readonly router: Router,
    private activatedRoute: ActivatedRoute,
    private readonly toaster: ToastrService,
    @Inject(CONFIG) public config: AutoRegistrationConfig,
  ) {}

  /** Digit → Token map */
  private readonly DIGIT_TOKEN_MAP: Record<string, string> = {
    '0': 'Zax',
    '1': 'Qxm',
    '2': 'Lmt',
    '3': 'Rty',
    '4': 'Yup',
    '5': 'Iok',
    '6': 'Pka',
    '7': 'Asd',
    '8': 'Dfg',
    '9': 'Ghj',
  };

  /** Token → Digit reverse map (generated once for performance) */
  private readonly REVERSE_TOKEN_MAP: Record<string, string> =
    Object.entries(this.DIGIT_TOKEN_MAP)
      .reduce((acc, [digit, token]) => {
        acc[token] = digit;
        return acc;
      }, {} as Record<string, string>);

  ngOnInit(): void {
    this.translate.onLangChange.subscribe((response) => {
      this.dir = response?.lang === 'en' ? 'ltr' : 'rtl';
    });

    this.handleTokenFromQuery();
    this.loadRegisteredPhases();
  }
  

  /** If current route is reset-password, return true, otherwise return false */
  get isResetPasswordRoute(): boolean {
    if (this.router.url.includes(this.config.routes.RESET_PASSWORD)) {
      this.router.navigate([this.config.routes.RESET_PASSWORD]);
      this.done();
      return true;
    }
    return false;
  }
  /** If current route is registration-id, navigate ti sign up and return true, otherwise return false */
  get isRegistrationId(): boolean {
    const regId = this.sharedService.getCookie('RegistrationCycleLogId');
    if (!regId) {
      this.router.navigate(['registration-cycle']);
      this.done();
      return true;
    }
    return false;
  }

  /**
   * Loads registered phases; on success:
   * - If Result has One, Two, Three but not Four → redirect to choose-business-3
   * - If Result has One, Two, Three, Four → redirect to smart-suggestions
   * Spinner and no content until response returns.
   */
  private loadRegisteredPhases(): void {
    this.spinner.show();

    if (this.isResetPasswordRoute) return;
    if (this.isRegistrationId) return;

    const regId = this.sharedService.getCookie('RegistrationCycleLogId');

    this.businessDetailsService
      .getRegisteredPhases({ RegistrationCycleLogId: +regId })
      .pipe(take(1))
      .subscribe({
        next: (res) => {
          console.log('res getRegisteredPhases:', res);
          const result = res?.Result;
          if (!result) {
            this.router.navigate(['registration-cycle']);
            this.done();
            return;
          }

          const hasOne = !!this.sharedService.userData;
          const hasTwo = !!this.sharedService.basicDetails;
          const hasThree = !!this.sharedService.businessDetails;
          const hasFour = !!this.sharedService.workHoursDetails;

          let route = '';
          if (hasOne && hasTwo && hasThree && this.sharedService.isMobileNumberVerified && !hasFour) {
            route = registrationRoutesConfig.CHOOSE_BUSINESS_5;
          } else if (hasOne && hasTwo && this.sharedService.isMobileNumberVerified && !hasFour) {
            route = registrationRoutesConfig.CHOOSE_BUSINESS_4;
          } else if (hasOne && this.sharedService.isMobileNumberVerified && !hasFour) {
            route = registrationRoutesConfig.CHOOSE_BUSINESS_3;
          } else if ((hasOne && !hasTwo) || !this.sharedService.isMobileNumberVerified) {
            route = registrationRoutesConfig.VERIFY;
          } else if (hasOne && hasTwo && hasThree && hasFour) {
            route = registrationRoutesConfig.SMART_SUGGESTIONS;
          }

          this.router.navigate([route]);
          this.done();
        },
        error: () => {
          this.done();
        },
      });
  }

  /** Marks phases check complete and hides spinner */
  private done(): void {
    this.phasesCheckDone = true;
    this.spinner.hide();
  }

  /**
   * Reads token from query params, decodes it,
   * and stores RegistrationCycleLogId in cookie if valid.
   */
  private handleTokenFromQuery(): void {
    const tokenParam = this.activatedRoute.snapshot.queryParamMap.get('token');
    const withToken = this.activatedRoute.snapshot.queryParamMap.get('withToken');
    if (withToken && withToken == 'false') {
      this.sharedService.clearCookie('RegistrationCycleLogId');
      return;
    };
    if (!tokenParam) return;

    const decodedValue: string = this.decodeToken(tokenParam);

    if (decodedValue) {
      const message = this.dir == 'ltr' 
        ? 'Sorry!! You Must Complete Your Registration Before Log In To The System !' 
        : 'عفوا !! يجب عليك إستكمال التسجيل قبل الدخول إلي النظام !';
      this.toaster.info(message);
      this.sharedService.setCookie('RegistrationCycleLogId', decodedValue, 30);
    }
  }

  /**
   * Decodes tokenized string back to original numeric string.
   * Example: IokGhjQxmDfg -> 5918
   */
  private decodeToken(tokenValue: string): string {
    return tokenValue
      .match(/.{1,3}/g)
      ?.map(token => this.REVERSE_TOKEN_MAP[token] ?? '')
      .join('') ?? '';
  }
}
